import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { resetPasswordWithToken } from "../api/user";
import ProcessingButton from "../components/Skeleton/ProcessingButton";
import PageContainer from "../components/pages/PageContainer";
import qushiLogo from "../images/logo-green.png";
import useAuthScreenBackground from "../hooks/useAuthScreenBackground";
import {
  PASSWORD_HINT,
  PASSWORD_ERROR,
  isStrongPassword,
} from "../utils/passwordRules";

const QushiLogo = () => (
  <img src={qushiLogo} alt="QUSHi" style={{ width: "120px" }} />
);

// Shown on the Sign In screen after the reset lands.
export const RESET_SUCCESS_NOTICE =
  "Your password has been reset. Sign in with your new password.";

// "This link has expired." is the backend's FORGOT_EMAIL_EXPIRED response.
const resetErrorMessage = (message) =>
  message === "This link has expired."
    ? "This reset link expired. Request a new one."
    : "Could not reset your password. Request a new link.";

function ResetPasswordToken() {
  // Cream all the way to the screen edges - see the hook for why the wrapper
  // div alone could not reach behind the status bar and home indicator.
  useAuthScreenBackground();

  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobileView] = useState(window.innerWidth < 768);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError({ password: "Enter a new password." });
      return;
    }
    if (!isStrongPassword(password)) {
      setError({ password: PASSWORD_ERROR });
      return;
    }
    if (password !== confirmPassword) {
      setError({ confirm: "Passwords do not match." });
      return;
    }

    setLoading(true);
    try {
      const response = await resetPasswordWithToken({
        token: token,
        password: password,
      });

      if (response?.data?.status === 200 || response?.data?.success) {
        // The confirmation belongs on the screen the user lands on, not on this
        // one they are leaving, so it is handed over in navigation state.
        navigate("/auth/login", {
          replace: true,
          state: { notice: RESET_SUCCESS_NOTICE },
        });
        return;
      }

      toast.error(resetErrorMessage(response?.data?.message));
    } catch (err) {
      console.error("Error:", err);
      toast.error(resetErrorMessage(err?.response?.data?.message));
    } finally {
      setLoading(false);
    }
  };

  // Mobile carries slightly heavier labels than desktop - see the matching note
  // on the Forgot Password screen.
  const labelClass = isMobileView
    ? "block text-gray-600 text-sm font-semibold tracking-wide mb-2 font-montserrat"
    : "block text-gray-500 text-xs tracking-wide mb-2 font-montserrat";

  const passwordField = (
    id,
    label,
    placeholder,
    value,
    onChange,
    visible,
    toggle,
    autoComplete,
  ) => (
    <>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <div className="relative">
        {/* size={1} - see the note on the Forgot Password screen. Without it an
            input's ~20-character intrinsic width can out-size the heading and
            widen the whole column. */}
        <input
          id={id}
          size={1}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (error) setError(null);
          }}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 pr-12 focus:outline-none"
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 focus:outline-none"
        >
          {visible ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
        </button>
      </div>
    </>
  );

  const form = (
    <form onSubmit={handleSubmit} className="w-full">
      <h1 className="text-primaryColor font-bold text-4xl mb-8 font-montserrat">
        Create a new password
      </h1>

      {passwordField(
        "new-password",
        "New password",
        "Enter new password",
        password,
        setPassword,
        showPassword,
        () => setShowPassword(!showPassword),
        "new-password",
      )}
      <p className="text-gray-500 text-xs mt-2 ml-2 font-montserrat">
        {PASSWORD_HINT}
      </p>
      {error?.password ? (
        <span role="alert" className="block text-red-500 text-xs mt-2 ml-2">
          {error.password}
        </span>
      ) : null}

      <div className="mt-6">
        {passwordField(
          "confirm-password",
          "Confirm new password",
          "Confirm new password",
          confirmPassword,
          setConfirmPassword,
          showConfirmPassword,
          () => setShowConfirmPassword(!showConfirmPassword),
          "new-password",
        )}
        {error?.confirm ? (
          <span role="alert" className="block text-red-500 text-xs mt-2 ml-2">
            {error.confirm}
          </span>
        ) : null}
      </div>

      <ProcessingButton
        type="submit"
        processing={loading}
        label="Reset Password"
        processingLabel="Resetting"
        className="w-full bg-primaryColor text-white rounded-2xl py-4 mt-6 font-bold text-lg focus:outline-none active:opacity-80 transition"
      />

      <div className="text-center mt-6">
        <Link
          to="/auth/login"
          className="text-primaryColor font-bold underline font-montserrat"
        >
          Back to Sign In
        </Link>
      </div>
    </form>
  );

  // Mobile: no left panel, no back arrow, wordmark below "Back to Sign In" and
  // in normal flow.
  if (isMobileView) {
    return (
      <PageContainer>
        <div
          className="min-h-screen w-full flex flex-col justify-center px-8 py-12"
          style={{ backgroundColor: "#F2F0EC" }}
        >
          {form}
          <div className="flex justify-center py-6 mt-2">
            <QushiLogo />
          </div>
        </div>
      </PageContainer>
    );
  }

  // Desktop: the same split screen as Forgot Password - full wordmark and
  // tagline on the dark teal panel, no square Q icon above the form. The column
  // is sized to its own heading for the same reason as that screen, so the
  // fields and CTA stay flush with the title rather than overhanging it.
  return (
    <PageContainer>
      <div className="flex min-h-screen">
        <div className="w-1/2 bg-primaryColor flex flex-col items-center justify-center">
          <img src="/Logo.png" alt="QUSHi" className="w-64" />
          <p
            className="text-white text-sm mt-4 uppercase whitespace-nowrap"
            style={{ letterSpacing: "0.3em", marginRight: "-0.3em" }}
          >
            TRADE TRACK PROVE IT
          </p>
        </div>
        <div
          className="w-1/2 flex items-center justify-center px-12 py-12 min-h-screen"
          style={{ backgroundColor: "#F2F0EC" }}
        >
          <div style={{ width: "max-content", maxWidth: "100%" }}>{form}</div>
        </div>
      </div>
    </PageContainer>
  );
}

export default ResetPasswordToken;
